import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { API, UPLOADS } from "./../../config/api";

export default function Machine() {
  const navigate = useNavigate();
  const location = useLocation();

  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [searchState, setSearchState] = useState("");
  const [price, setPrice] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [wishlist, setWishlist] = useState([]);
  const [user, setUser] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(40);

  // Helper to reset all filters
  const clearAllFilters = () => {
    setSearch("");
    setSearchState("");
    setPrice("");
    setMinPrice("");
    setMaxPrice("");
    setCurrentPage(1);
  };

  const fetchMachines = useCallback(async () => {
    try {
      const res = await fetch(
        `${API.LIST_MACHINES}&status=Active&t=${new Date().getTime()}`,
      );
      const data = await res.json();
      setProducts(data);
    } catch (error) {
      console.log("Fetch Error:", error);
    }
  }, []);

  useEffect(() => {
    fetchMachines();
    const interval = setInterval(fetchMachines, 5000);
    window.addEventListener("focus", fetchMachines);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", fetchMachines);
    };
  }, [fetchMachines]);

  const loadUserAndWishlist = useCallback(() => {
    const loginUser = JSON.parse(sessionStorage.getItem("user"));
    setUser(loginUser);
    if (loginUser) {
      const savedWishlist =
        JSON.parse(localStorage.getItem(`wishlist_${loginUser.id}`)) || [];
      setWishlist(savedWishlist);
    } else {
      setWishlist([]);
    }
  }, []);

  useEffect(() => {
    loadUserAndWishlist();
    window.addEventListener("cartUpdated", loadUserAndWishlist);
    window.addEventListener("storage", loadUserAndWishlist);
    return () => {
      window.removeEventListener("cartUpdated", loadUserAndWishlist);
      window.removeEventListener("storage", loadUserAndWishlist);
    };
  }, [loadUserAndWishlist]);

  /* FILTER LOGIC */
  useEffect(() => {
    let filtered = [...products];

    if (search) {
      filtered = filtered.filter((item) =>
        item.city?.toLowerCase().includes(search.toLowerCase()),
      );
    }

    if (searchState) {
      filtered = filtered.filter((item) =>
        item.state?.toLowerCase().includes(searchState.toLowerCase()),
      );
    }

    if (minPrice) {
      filtered = filtered.filter(
        (item) => Number(item.price) >= Number(minPrice),
      );
    }
    if (maxPrice) {
      filtered = filtered.filter(
        (item) => Number(item.price) <= Number(maxPrice),
      );
    }

    setFilteredProducts(filtered);
    setCurrentPage(1);
  }, [search, searchState, price, products, minPrice, maxPrice]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentMachines = filteredProducts.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleLike = (machine) => {
    if (!user) {
      alert("Please login first");
      return;
    }
    const storageKey = `wishlist_${user.id}`;
    let updatedWishlist = [...wishlist];
    const exists = updatedWishlist.find((item) => item.id === machine.id);

    if (exists) {
      updatedWishlist = updatedWishlist.filter(
        (item) => item.id !== machine.id,
      );
    } else {
      updatedWishlist.push({ ...machine, type: "machine" });
    }

    setWishlist(updatedWishlist);
    localStorage.setItem(storageKey, JSON.stringify(updatedWishlist));
    window.dispatchEvent(new Event("cartUpdated"));
  };

  return (
    <div className="shop-page container-fluid py-4">
      <div className="row">
        {/* LEFT FILTER (Desktop) */}
        <div className="col-lg-2 d-none d-lg-block">
          <div className="filter-sidebar shadow-sm p-3 bg-white rounded sticky-top">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">Filters</h5>
              <button
                className="btn btn-sm text-danger p-0"
                onClick={clearAllFilters}
                style={{ fontSize: "0.8rem", fontWeight: "600" }}
              >
                CLEAR ALL
              </button>
            </div>

            <div className="mt-3">
              <h6 className="small fw-bold">Custom Price</h6>
              <input
                type="number"
                placeholder="Min Price"
                className="form-control mb-2 form-control-sm"
                value={minPrice}
                onChange={(e) => setMinPrice(e.target.value)}
              />
              <input
                type="number"
                placeholder="Max Price"
                className="form-control form-control-sm"
                value={maxPrice}
                onChange={(e) => setMaxPrice(e.target.value)}
              />
            </div>

            <div className="filter-box mt-4">
              <h6 className="small fw-bold">City</h6>
              <input
                type="text"
                placeholder="Search City"
                className="form-control form-control-sm"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className="filter-box mt-4">
              <h6 className="small fw-bold">State</h6>
              <input
                type="text"
                placeholder="Search State"
                className="form-control form-control-sm"
                value={searchState}
                onChange={(e) => setSearchState(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* MOBILE HORIZONTAL FILTER */}
        <div className="d-lg-none mb-3 px-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="small fw-bold text-muted">Quick Filters</span>
            <button
              className="btn btn-sm btn-link text-danger p-0"
              onClick={clearAllFilters}
            >
              Clear All
            </button>
          </div>
          <div className="mobile-filter-bar d-flex gap-2 overflow-auto pb-2">
            <input
              type="number"
              placeholder="Min ₹"
              className="form-control form-control-sm"
              style={{ minWidth: "100px" }}
              value={minPrice}
              onChange={(e) => setMinPrice(e.target.value)}
            />
            <input
              type="number"
              placeholder="Max ₹"
              className="form-control form-control-sm"
              style={{ minWidth: "100px" }}
              value={maxPrice}
              onChange={(e) => setMaxPrice(e.target.value)}
            />
            <input
              type="text"
              placeholder="City"
              className="form-control form-control-sm"
              style={{ minWidth: "100px" }}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <input
              type="text"
              placeholder="State"
              className="form-control form-control-sm"
              style={{ minWidth: "100px" }}
              value={searchState}
              onChange={(e) => setSearchState(e.target.value)}
            />
          </div>
        </div>

        {/* PRODUCTS GRID */}
        <div className="col-lg-8">
          <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
            <h2 className="section-title mb-0">Embroidery Machines</h2>
            <div className="d-flex align-items-center gap-2">
              <span className="small text-muted">Show:</span>
              <select
                className="form-select form-select-sm"
                style={{ width: "80px" }}
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(parseInt(e.target.value))}
              >
                <option value={20}>20</option>
                <option value={40}>40</option>
                <option value={60}>60</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>

          <div className="designs-grid">
            {currentMachines.length > 0 ? (
              currentMachines.map((item) => {
                const images = item.image ? item.image.split(",") : [];
                const isLiked = wishlist.some((w) => w.id === item.id);

                return (
                  <div className="design-card" key={item.id}>
                    <div className="machine-img-container">
                      <img
                        src={`${UPLOADS.MACHINE_IMAGE}${item.user_id}/${images[0]}?t=${Date.now()}`}
                        alt={item.machine_name}
                      />
                      <span className="heads-badge">
                        {item.NoOfHeads} Heads
                      </span>
                    </div>

                    <div className="machine-body">
                      <div className="machine-type">
                        <h4 className="machine-name">{item.machine_name}</h4>
                      </div>
                      <div className="d-flex align-items-center justify-content-between mt-2">
                        <div className="price-block">
                          <span className="currency-symbol">₹</span>
                          <span className="amount">
                            {Number(item.price).toLocaleString("en-IN")}
                          </span>
                        </div>
                        <button
                          className="contact-btn"
                          onClick={() => navigate(`/productdetails/${item.id}`)}
                        >
                          <i className="bi bi-telephone-fill me-1"></i> Contact
                        </button>
                      </div>
                      <div className="spec-row mt-2">
                        <div className="spec-item">
                          <span className="spec-label">Brand:</span>
                          <span className="spec-value">{item.brand}</span>
                        </div>
                        <div className="spec-item">
                          <span className="spec-label">Model:</span>
                          <span className="spec-value">{item.model}</span>
                        </div>
                      </div>
                      <div className="location-info">
                        <i className="bi bi-geo-alt-fill"></i> {item.city},{" "}
                        {item.state}
                      </div>
                      <div className="machine-footer mt-1">
                        <button
                          className="more-details-btn"
                          onClick={() => navigate(`/productdetails/${item.id}`)}
                        >
                          <i className="bi bi-info-circle me-2"></i> More
                          Details
                        </button>
                        <button
                          className="footer-like-btn"
                          onClick={() => toggleLike(item)}
                        >
                          <i
                            className={`bi ${isLiked ? "bi-heart-fill text-danger" : "bi-heart"}`}
                          ></i>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center w-100 py-5">
                <i
                  className="bi bi-search text-muted mb-3"
                  style={{ fontSize: "3rem" }}
                ></i>
                <p className="text-muted">
                  No machines found matching your criteria.
                </p>
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={clearAllFilters}
                >
                  Clear all filters
                </button>
              </div>
            )}
          </div>

          {/* PAGINATION */}
          {totalPages > 1 && (
            <nav className="d-flex justify-content-center mt-5">
              <ul className="pagination custom-pagination">
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => paginate(currentPage - 1)}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>
                </li>
                {[...Array(totalPages)].map((_, i) => (
                  <li
                    key={i + 1}
                    className={`page-item ${currentPage === i + 1 ? "active" : ""}`}
                  >
                    <button
                      className="page-link"
                      onClick={() => paginate(i + 1)}
                    >
                      {i + 1}
                    </button>
                  </li>
                ))}
                <li
                  className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => paginate(currentPage + 1)}
                  >
                    <i className="bi bi-chevron-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          )}
        </div>

        {/* RIGHT ADS */}
        <div className="col-lg-2">
          <div className="ads-sidebar p-3 bg-white shadow-sm rounded text-center sticky-top">
            <h5 className="ads-title">Sponsored</h5>
            <div className="ad-card mt-3">
              <div className="ad-placeholder">
                <p className="small text-muted">Your Ad Here</p>
              </div>
              <p className="small mt-2">Premium Threads Sale!</p>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .custom-pagination .page-link {
          color: #00a699;
          border: 1px solid #dee2e6;
          margin: 0 3px;
          border-radius: 4px;
          font-weight: 500;
        }
        .custom-pagination .page-item.active .page-link {
          background-color: #00a699;
          border-color: #00a699;
          color: white;
        }
        .custom-pagination .page-link:hover {
          background-color: #f1f1f1;
          color: #00847a;
        }
        .mobile-filter-bar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
}
